import React, { useState } from "react";
import { T } from "services/lang/LanguageService";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { ContextTypes } from "api/model/dashboard/DashboardTab";
import { Button } from "primereact/button";
import useDashboardStore from "store/dashboard-store";
import { adjustReferenceTime } from "../../utils";

interface TabContextMenuProps {
  tabId: number;
}

const TabContextMenu = ({ tabId }: TabContextMenuProps) => {
  const tabContextConfig = useDashboardStore(state => state.getTabContextConfig(tabId) || {});
  const setTabContextConfig = useDashboardStore(state => state.setTabContextConfig);
  const triggerTabRefresh = useDashboardStore(state => state.triggerTabRefresh);

  const adjustedTime = adjustReferenceTime(tabContextConfig.config);

  const [from, setFrom] = useState<Date>(
    tabContextConfig.from
      ? new Date(tabContextConfig.from)
      : new Date(adjustedTime.dateFrom || Date.now())
  );
  const [to, setTo] = useState<Date>(
    tabContextConfig.to
      ? new Date(tabContextConfig.to)
      : new Date(adjustedTime.dateTo || Date.now())
  );
  const [referenceTime, setReferenceTime] = useState<Date>(
    tabContextConfig.referenceTime
      ? new Date(tabContextConfig.referenceTime)
      : new Date(adjustedTime.referenceTime || Date.now())
  );

  const updateStore = () => {
    setTabContextConfig(tabId, {
      ...tabContextConfig,
      from: from.toISOString(),
      to: to.toISOString(),
      referenceTime: referenceTime.toISOString(),
    });
    triggerTabRefresh();
  };

  return (
    <div className="flex flex-row flex-wrap gap-2">
      {tabContextConfig?.contextType === ContextTypes.RANGE_DATE_SELECTION && (
        <div className="flex flex-row flex-wrap gap-2">
          <div className="flex flex-column gap-2">
            <label htmlFor="Date From">{T("word.date-from")}</label>
            <div className="flex flex-row">
              <CalendarWrapper
                id="dateFrom"
                value={from}
                onChange={e => setFrom(new Date(e))}
                showTime
                className="w-3 align-self-center"
                debounceTimeInMs={100}
              />
            </div>
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor="Date To">{T("word.date-to")}</label>
            <div className="flex flex-row">
              <CalendarWrapper
                id="dateTo"
                value={to}
                onChange={e => setTo(new Date(e))}
                showTime
                className="w-3 align-self-center"
                debounceTimeInMs={100}
              />
            </div>
          </div>
        </div>
      )}
      {(tabContextConfig?.contextType === ContextTypes.SINGLE_DATE_SELECTION_WITH_CONFIG ||
        tabContextConfig?.contextType === ContextTypes.SINGLE_DATE_SELECTION) && (
        <div className="flex flex-column gap-2">
          <label htmlFor="referenceTime">{T("load-conf.reference-time")}</label>
          <div className="flex flex-row">
            <CalendarWrapper
              id="referenceTime"
              value={referenceTime}
              onChange={e => setReferenceTime(new Date(e))}
              className="w-3 align-self-center"
              debounceTimeInMs={100}
            />
          </div>
        </div>
      )}
      {tabContextConfig?.contextType === ContextTypes.YEARS_SELECTION && (
        <div className="flex flex-column gap-2">
          <label htmlFor="years">{T("time.years")}</label>
          <div className="flex flex-row">
            <CalendarWrapper
              id="years"
              view="year"
              value={from}
              onChange={e => {
                const selectedYear = e.getFullYear();
                setFrom(new Date(selectedYear, 0, 1));
                const newTo = new Date(selectedYear + 1, 0, 1);
                newTo.setMinutes(newTo.getMinutes() - 1);
                setTo(newTo);
              }}
              dateFormat="yy"
              className="w-3 align-self-center"
              debounceTimeInMs={100}
              showTime={false}
            />
          </div>
        </div>
      )}
      <Button
        icon="pi pi-play"
        tooltip={T("action.fetch-data")}
        tooltipOptions={{ position: "bottom" }}
        onClick={updateStore}
        severity="success"
        className="h-3rem align-self-center mt-3"
      />
    </div>
  );
};

export default TabContextMenu;
